import React, { useState } from 'react';
import { Mail, ArrowRight, CheckCircle2, Bell, AlertCircle, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
export function Newsletter() {
  const {
    t
  } = useLanguage();
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const {
    ref,
    isVisible
  } = useScrollAnimation({
    threshold: 0.2
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    if (!consent) {
      setError(t.newsletter?.consentRequired || 'Consent required');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    setError(null);
    setStatus('loading');
    setTimeout(() => {
      setStatus('success');
      setEmail('');
    }, 1500);
  };
  return <section className="py-24 bg-[#050505] border-t border-white/5 transition-colors duration-300" data-id="element-303">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-304">
        <div ref={ref} className={`relative overflow-hidden rounded-3xl bg-accent-blue/10 p-8 md:p-12 lg:p-16 border border-white/10 animate-hidden animate-scale-in ${isVisible ? 'animate-visible' : ''}`} data-id="element-305">
          {/* Decorative background elements */}
          <div className="hidden md:block absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-accent-blue/10 rounded-full blur-3xl" data-id="element-306" />
          <div className="hidden md:block absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" data-id="element-307" />

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8 md:gap-12" data-id="element-308">
            {/* Left Content */}
            <div className="max-w-xl text-center lg:text-left w-full" data-id="element-309">
              <div className="flex flex-col md:flex-row items-center justify-center lg:justify-start gap-4 mb-4" data-id="element-310">
                <div className="p-3 bg-accent-blue/10 rounded-2xl shadow-lg shadow-accent-blue/20" data-id="element-311">
                  <Bell className="w-8 h-8 md:w-10 md:h-10 text-accent-blue" data-id="element-312" />
                </div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white" data-id="element-313">
                  {t.newsletter?.title || 'Stay ahead of the AI curve'}
                </h2>
              </div>
              <p className="text-base md:text-lg text-gray-300" data-id="element-314">
                {t.newsletter?.subtitle || 'Get the latest AI website builder reviews, tips, and exclusive offers delivered to your inbox.'}
              </p>
            </div>

            {/* Right Form */}
            <div className="w-full max-w-md" data-id="element-315">
              {status === 'success' ? <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-6 text-center flex flex-col items-center" data-id="element-316">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4" data-id="element-317">
                    <CheckCircle2 className="w-8 h-8 text-green-500" data-id="element-318" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-319">
                    {t.newsletter?.success || 'Subscribed!'}
                  </h3>
                  <p className="text-gray-300 text-sm" data-id="element-320">
                    {t.newsletter?.successMessage || 'Check your email for confirmation.'}
                  </p>
                </div> : <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-id="element-321">
                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-322">
                    <div className="relative flex-grow" data-id="element-323">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-324">
                        <Mail className="h-5 w-5 text-gray-400" data-id="element-325" />
                      </div>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t.newsletter?.placeholder || 'Enter your email'} className="w-full pl-11 pr-4 py-3.5 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-accent-blue focus:border-transparent outline-none transition-all text-white placeholder-gray-400" required data-id="element-326" />
                    </div>
                    <button type="submit" disabled={status === 'loading'} className={`px-6 py-3.5 rounded-xl font-semibold text-white transition-all duration-300 flex items-center justify-center gap-2 bg-accent-blue hover:bg-accent-hover shadow-lg shadow-accent-blue/25 hover:shadow-xl hover:shadow-accent-blue/35 ${status === 'loading' ? 'opacity-80 cursor-wait' : ''}`} data-id="element-327">
                      {status === 'loading' ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-328" /> : <>
                          {t.newsletter?.button || 'Subscribe'}
                          <ArrowRight className="w-4 h-4" data-id="element-329" />
                        </>}
                    </button>
                  </div>

                  {/* Consent Checkbox */}
                  <div className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${shake ? 'animate-shake' : ''} ${error ? 'bg-red-500/10 border border-red-500/30' : 'hover:bg-white/5'}`} data-id="element-330">
                    <div className="relative flex items-center pt-0.5" data-id="element-331">
                      <input type="checkbox" id="consent" checked={consent} onChange={e => {
                    setConsent(e.target.checked);
                    if (e.target.checked) setError(null);
                  }} className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-gray-500 bg-black/40 transition-all checked:border-accent-blue checked:bg-accent-blue hover:border-accent-blue focus:ring-2 focus:ring-accent-blue/50" data-id="element-332" />
                      <Check className="pointer-events-none absolute left-1/2 top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 transition-opacity peer-checked:opacity-100" data-id="element-333" />
                    </div>
                    <label htmlFor="consent" className="text-xs text-gray-400 cursor-pointer select-none leading-relaxed" data-id="element-334">
                      {t.newsletter?.consent || 'I agree to the processing of personal data'}
                    </label>
                  </div>

                  {error && <div className="flex items-center gap-2 text-red-400 text-xs px-1" data-id="element-335">
                      <AlertCircle className="w-3 h-3" data-id="element-336" />
                      <span data-id="element-337">{error}</span>
                    </div>}

                  <p className="text-xs text-gray-500 text-center" data-id="element-338">
                    {t.newsletter?.privacy || 'Unsubscribe anytime.'}
                  </p>
                </form>}
            </div>
          </div>
        </div>
      </div>
    </section>;
}